import { useCallback, useEffect, useMemo, useState } from "react";
import { Chevron } from "../../Icons";
import style from "./Inputs.module.css";
import { useTranslation } from "../../../hooks/useTranslation";
import { apply, type RulesLogic } from "json-logic-js";

const InputSelect = ({
  props,
  dependantConditions,
  selectOptions,
  label,
  watchedInputs,
  isRequired = false,
  setValue,
  error,
}: ComponentPropsType) => {
  const { locale } = useTranslation();
  const [menuValue, setMenuValue] = useState(props.value || "no_option");

  useEffect(() => {
    setMenuValue(props.value);
  }, [props.value]);

  const onSelectOptionHandler = useCallback(
    (value) => {
      const e = {
        target: {
          name: props.name,
          value,
        },
      };

      if (props.onChange) {
        props.onChange(e);
      }

      if (dependantConditions && dependantConditions.length > 0 && setValue) {
        dependantConditions.forEach((condition: any) => {
          setValue(condition.name, condition.value[e.target.value] ?? "no_option");
        });
      }
    },
    [props, setValue, dependantConditions],
  );

  const onCloseMenuHandler = useCallback((e) => {
    const allMenus: HTMLElement[] = Array.from(document.querySelectorAll(".select_menu"));
    if (!e.target.closest(".select_menu") && !e.target.closest(`.${style.option_disabled}`)) {
      allMenus.forEach((menu) => {
        if (menu.classList.contains(style.select_menu_active)) {
          menu.classList.remove(style.select_menu_active);
        }
      });
    }
  }, []);

  const onToggleMenuHandler = useCallback((e) => {
    const targetMenu = e.target.closest(".select_menu");
    const allMenus: HTMLElement[] = Array.from(document.querySelectorAll(".select_menu"));
    allMenus.forEach((menu) => {
      if (menu !== targetMenu) {
        if (menu.classList.contains(style.select_menu_active)) {
          menu.classList.remove(style.select_menu_active);
        }
      }
    });
    if (targetMenu) {
      if (targetMenu.classList.contains(style.select_menu_active)) {
        targetMenu.classList.remove(style.select_menu_active);
      } else {
        targetMenu.classList.add(style.select_menu_active);
      }
    }
  }, []);

  const activeOption = useMemo(() => {
    const active = {
      name: locale("selectInputSelect") as string,
      value: "no_option",
      image: "",
    };
    selectOptions.forEach((item) => {
      if (item.value === menuValue) {
        active.name = item.label;
        active.value = item.value;
        active.image = item.image || "";
      }
    });
    return active;
  }, [locale, menuValue, selectOptions]);

  useEffect(() => {
    document.addEventListener("click", onCloseMenuHandler);
    return () => {
      document.removeEventListener("click", onCloseMenuHandler);
    };
  }, [onCloseMenuHandler]);

  // filter available options for the select menu based on the rese of the form data
  const refinedOptions = selectOptions.filter((option) => {
    let orFlag = false,
      andFlag = true,
      allFlag = true;
    if (option.dependsOn && Array.isArray(option.dependsOn)) {
      allFlag = option.dependsOn.reduce((acc: any, cur: any) => acc && watchedInputs[cur.name] === cur.value, true);
    } else if (option.dependsOn && "jsonLogic" in option.dependsOn) {
      allFlag = apply(option.dependsOn.rules, watchedInputs);
    } else if (option.dependsOn) {
      if (option.dependsOn?.or)
        orFlag = option.dependsOn.or.reduce(
          (acc: any, cur: any) => acc || watchedInputs[cur.name] === cur.value,
          false,
        );
      if (option.dependsOn?.and)
        andFlag = option.dependsOn.and.reduce(
          (acc: any, cur: any) => acc && watchedInputs[cur.name] === cur.value,
          true,
        );
      if (option.dependsOn?.or && option.dependsOn?.and) allFlag = orFlag && andFlag;
      else if (option.dependsOn?.or) allFlag = orFlag;
      else if (option.dependsOn?.and) allFlag = andFlag;
    }
    return allFlag;
  });

  return (
    <div className={style.input_container} ref={props?.ref}>
      {label && (
        <label>
          {label}
          {!!isRequired && <span className={style.reqFeild}>*</span>}
        </label>
      )}
      <div className={`${style.select_menu} select_menu`} onClick={onToggleMenuHandler}>
        <div>
          {activeOption.image && <img src={activeOption.image} alt="" />}
          {activeOption.name}
        </div>
        <Chevron width="11" height="11" color="#000" />
      </div>
      <div className={style.all_options}>
        <div
          className={`${style.option}${activeOption.value === "no_option" ? ` ${style.option_active}` : ""}`}
          onClick={() => (activeOption.value === "no_option" ? {} : onSelectOptionHandler("no_option"))}
        >
          {locale("selectInputSelectOption")}
        </div>
        {refinedOptions.map((option) => (
          <div
            key={option.value}
            className={`${style.option}${activeOption.value === option.value ? ` ${style.option_active}` : ""}`}
            onClick={() => (activeOption.value === option.value ? {} : onSelectOptionHandler(option.value))}
          >
            {option.image && <img src={option.image} alt="" />}
            {option.label}
          </div>
        ))}
      </div>
      {error && (
        <span className={style.input_note} style={{ color: "#dc0000" }}>
          {error}
        </span>
      )}
    </div>
  );
};

export default InputSelect;

export type SelectOption = {
  label: string;
  value: any;
  image?: string;
  /**
   * if array is provided, it will check if all conditions in the array are met
   * with object will do `OR && AND`, where the we need one condition to be met in the `OR` field and all conditions to be met in the `AND` field
   */
  dependsOn?:
    | {
        jsonLogic: true;
        rules: RulesLogic;
      }
    | {
        or?: {
          name: string;
          value: any;
        }[];
        and?: {
          name: string;
          value: any;
        }[];
      }
    | {
        name: string;
        value: any;
      }[];
};

type ComponentPropsType = {
  props: any;
  selectOptions: SelectOption[];
  dependantConditions?: {
    name: string;
    value: any;
  }[];
  label?: string;
  watchedInputs?: any;
  isRequired?: boolean;
  setValue?: CallableFunction;
  error?: any;
};
